import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useSnackbar } from 'react-simple-snackbar'
import { previewVirementExport, downloadVirementExport } from '../../api/index'
import { useRhGuard } from '../../hooks/useRoleGuard'
import { formatTND } from '../../utils/formatTND'
import Spinner from '../Spinner/Spinner'
import styles from './VirementMensuel.module.css'

const MONTHS = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Février' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' },
]

const triggerBlobDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  window.URL.revokeObjectURL(url)
}

const VirementMensuel = () => {
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useRhGuard()

  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())
  const [preview, setPreview] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState('')
  const [downloading, setDownloading] = useState(false)

  const periodLabel = `${MONTHS.find((m) => m.value === month)?.label} ${year}`

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true)
    setPreviewError('')
    setPreview(null)
    try {
      const { data } = await previewVirementExport({ month, year })
      setPreview(data)
    } catch (error) {
      setPreviewError(error?.response?.data?.message || 'Impossible de charger l\'aperçu virement')
    } finally {
      setPreviewLoading(false)
    }
  }, [month, year])

  useEffect(() => {
    loadPreview()
  }, [loadPreview])

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const { data } = await downloadVirementExport({ month, year })
      const slug = `${year}_${String(month).padStart(2, '0')}`
      triggerBlobDownload(data, `virement-biat-${slug}.xls`)
      openSnackbarRef.current('Fichier virement MyBIAT téléchargé')
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur téléchargement virement')
    } finally {
      setDownloading(false)
    }
  }

  const canDownload = preview?.beneficiaryCount > 0 && preview?.ribDonneurConfigured && !downloading

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1>Virement mensuel</h1>
          <p>
            Générez le fichier Excel BIAT pour effectuer les virements aux coaches sur MyBIAT.
          </p>
        </div>
      </div>

      <div className={styles.panel}>
        <h2>Période</h2>
        <p className={styles.hint}>
          Le fichier est généré directement depuis le modèle BIAT officiel du projet
          (logo, couleurs, dimensions identiques), rempli avec les coaches du mois.
        </p>

        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label htmlFor="virement-month">Mois</label>
            <select
              id="virement-month"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              disabled={downloading}
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="virement-year">Année</label>
            <input
              id="virement-year"
              type="number"
              min="2020"
              max="2100"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              disabled={downloading}
            />
          </div>
        </div>
      </div>

      <div className={styles.panel}>
        <h2>Aperçu virement MyBIAT</h2>

        {previewLoading ? (
          <div className={styles.loadingRow}>
            <Spinner />
            <span>Analyse virement {periodLabel}…</span>
          </div>
        ) : previewError ? (
          <p className={styles.error}>{previewError}</p>
        ) : preview ? (
          <>
            {!preview.ribDonneurConfigured && (
              <p className={styles.error}>
                RIB donneur d&apos;ordre non configuré sur le serveur (variable VIREMENT_RIB_DONNEUR).
              </p>
            )}

            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Bénéficiaires</span>
                <span className={styles.statValue}>{preview.beneficiaryCount}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Montant total</span>
                <span className={styles.statValue}>{formatTND(preview.totalTnd)} TND</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Sans RIB</span>
                <span className={styles.statValue}>{preview.missingRibCount}</span>
              </div>
            </div>

            <p className={styles.hint}>
              Donneur d&apos;ordre : <strong>{preview.raisonSociale}</strong>
              {' · '}
              Référence : <strong>{preview.reference}</strong>
              {' · '}
              Échéance : <strong>{preview.dueDate}</strong>
            </p>

            {preview.missingRibCount > 0 && (
              <div className={styles.warn}>
                <strong>{preview.missingRibCount}</strong> coach(es) sans RIB — exclus du fichier :
                <ul className={styles.missingList}>
                  {preview.missingRib.slice(0, 8).map((row) => (
                    <li key={row.invoiceId}>
                      {row.coachName} ({row.invoiceNumber})
                    </li>
                  ))}
                  {preview.missingRibCount > 8 && (
                    <li>… et {preview.missingRibCount - 8} autre(s)</li>
                  )}
                </ul>
              </div>
            )}

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.downloadBtn}
                disabled={!canDownload}
                onClick={handleDownload}
              >
                {downloading ? 'Génération…' : 'Télécharger fichier MyBIAT (.xls)'}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}

export default VirementMensuel
